/* Live Mainfreight freight calculator — freight-as-product flow.
   Quotes the cart via the hf-freight-connector service, then adds the hidden
   $1 Freight product (qty = dollars). Its 20,000kg marker weight unlocks the
   $0 "Freight included" rate at checkout.

   Structure (one IIFE, ES5 only — Shopify minifies on the CDN):
     1. Shared helpers + module state (incl. 1s cart microcache)
     1b. refreshCart — in-place drawer/cart refresh via the theme's morph
     2. Client-side suburb index (no network per keystroke)
     2c. Quote cache — repeat destination+goods combos paint instantly
     3. reloadWithDrawer + drawer reopen (fallback only — refreshCart first)
     4. enforceFreight — freight line follows the cart, always
     5. Document-level singletons: checkout intercept, click-away, pageshow
     6. Recovery nets: body MutationObserver + slow re-wire interval
     7. initFreightWidget — per-widget wiring (no document/window listeners)

   Hard-won rules (do not regress):
   - Wired-marker is a JS PROPERTY on the search input; Horizon's morphs copy
     attributes onto fresh nodes but properties never survive replacement.
   - Never attach observers to theme-owned elements (sections get replaced
     whole); observe document.body.
   - Repaints must never steal the widget from an engaged customer, and every
     state paint holds off while a mode switch is writing to the cart.
   - Any element that both has a display class and gets [hidden]-toggled needs
     an explicit [hidden]{display:none} rule in the snippet CSS.
   - Never dispatch 'cart:update' without stripping auto-open from
     cart-drawer-component first: Horizon's drawer treats EVERY cart:update as
     an add-to-cart and would pop itself open. */
(function () {
  // The script is included from BOTH the cowork drawer section (always in the
  // initial HTML — a page that loads with an empty cart gets the widget HTML
  // morphed in later, and morph-inserted <script> tags never execute) and the
  // widget snippet (covers non-drawer carts). Run once.
  if (window.__hfFreightLoaded) return;
  window.__hfFreightLoaded = true;

  // ---- 1. shared helpers + module state ----
  function getJSON(path) {
    return fetch(path).then(function (r) { return r.json(); });
  }
  function postJSON(path, body) {
    // Any cart write invalidates the cart microcache — before AND after, so
    // neither a mid-flight read nor a post-write read can serve stale truth.
    var isCartWrite = path.indexOf('/cart/') === 0;
    if (isCartWrite) bustCart();
    return fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      if (isCartWrite) bustCart();
      return r.json();
    });
  }
  function removeLines(lines) {
    if (!lines.length) return Promise.resolve();
    return postJSON('/cart/change.js', { id: lines[0].key, quantity: 0 }).then(function () {
      return removeLines(lines.slice(1));
    });
  }
  function setAttrs(a) { return postJSON('/cart/update.js', { attributes: a }); }
  function freightLinesOf(cart, variant) {
    return cart.items.filter(function (i) { return i.variant_id === variant; });
  }
  function realItemsOf(cart, variant) {
    return cart.items.filter(function (i) { return i.variant_id !== variant; });
  }
  function fingerprintOf(cart, variant) {
    return realItemsOf(cart, variant)
      .map(function (i) { return i.variant_id + 'x' + i.quantity; })
      .sort()
      .join('|');
  }
  function quoteItemsOf(cart, variant) {
    return realItemsOf(cart, variant).map(function (i) {
      return { name: i.product_title + ' ' + (i.variant_title || ''), sku: i.sku || '', quantity: i.quantity, grams: i.grams };
    });
  }
  function anyWidget() { return document.querySelector('.hf-freight'); }

  // The recovery nets + repaints all read the cart constantly; a 1-second
  // microcache with in-flight dedupe collapses those bursts into one fetch.
  // Money paths (checkout gate, add-freight) pass force=true and always see
  // the real cart; every write and every theme cart event busts the cache.
  var cartCache = { t: 0, promise: null };
  var lastCartFp = null; // latest known goods fingerprint (optimistic quotes)
  function getCart(force) {
    if (!force && cartCache.promise && Date.now() - cartCache.t < 1000) return cartCache.promise;
    cartCache.t = Date.now();
    var p = getJSON('/cart.js').then(function (c) {
      var w = anyWidget();
      if (w) lastCartFp = fingerprintOf(c, Number(w.dataset.variant));
      return c;
    });
    p.catch(function () { if (cartCache.promise === p) cartCache.promise = null; });
    cartCache.promise = p;
    return p;
  }
  function bustCart() { cartCache.promise = null; }

  // While a Delivery/Pickup switch is writing to the cart, every repaint holds
  // off — a drawer refresh mid-write would otherwise rebuild the widget from
  // not-yet-written cart state and flip the customer's choice back.
  var modeSwitchBusy = false;
  // Rapid tab clicks queue their cart writes in CLICK ORDER — parallel writes
  // race over ~1s RTTs and the earlier one can land last, ending the cart on
  // the wrong mode. The busy flag holds until the whole queue drains.
  var modeWriteChain = Promise.resolve();
  var pendingSwitches = 0;
  // Repaint request to all wired widget copies (root-level hf-sync listeners).
  function syncWidgets() {
    document.querySelectorAll('.hf-freight').forEach(function (el) {
      el.dispatchEvent(new CustomEvent('hf-sync'));
    });
  }

  // ---- 1b. in-place cart refresh (replaces the old full-page reloads) ----
  // Dispatching 'cart:update' makes every cart-items-component re-render its
  // own section through the theme's Section Rendering API and morph the fresh
  // HTML in — the drawer stays open (morph.js preserves dialog.open), nothing
  // flashes, and the freight line/totals/widget all update together. The
  // drawer's auto-open attribute comes off for the dispatch (its listener
  // runs synchronously) so a background fix can never pop the drawer open.
  function refreshCart(cart) {
    bustCart();
    if (!document.querySelector('cart-items-component')) {
      // No theme cart surface to morph (unexpected template) — honest reload.
      reloadWithDrawer();
      return;
    }
    var stripped = [];
    document.querySelectorAll('cart-drawer-component[auto-open]').forEach(function (d) {
      d.removeAttribute('auto-open');
      stripped.push(d);
    });
    var w = anyWidget();
    var goodsCount = 0;
    if (cart && cart.items && w) {
      realItemsOf(cart, Number(w.dataset.variant)).forEach(function (i) { goodsCount += i.quantity; });
    }
    try {
      document.dispatchEvent(new CustomEvent('cart:update', {
        detail: {
          resource: cart || {},
          sourceId: 'hf-freight-widget',
          // itemCount feeds the header bubble — goods only, never the
          // freight line's dollars-as-units quantity.
          data: { source: 'hf-freight-widget', itemCount: goodsCount },
        },
      }));
    } finally {
      stripped.forEach(function (d) { d.setAttribute('auto-open', ''); });
    }
    // The morphed-in widget HTML is the server's DEFAULT (form) state, and a
    // morph that preserves the wired input in place re-runs nothing — repaint
    // every copy from cart truth once the section swap has landed. Both are
    // engagement-guarded (hf-sync path), so nobody's typing gets stolen.
    setTimeout(syncWidgets, 700);
    setTimeout(syncWidgets, 1600);
  }

  // ---- 2. client-side suburb index ----
  // One lazy fetch of a pre-filtered "Suburb|ST|4032" list from the theme CDN,
  // then every keystroke matches locally: instant, and immune to network blips.
  var suburbsPromise = null;
  function loadSuburbs() {
    if (suburbsPromise) return suburbsPromise;
    var w = anyWidget();
    var url = w && w.dataset.suburbs;
    if (!url) return Promise.reject(new Error('no suburbs url'));
    suburbsPromise = fetch(url).then(function (r) {
      if (!r.ok) throw new Error('suburbs http ' + r.status);
      return r.text();
    }).then(function (text) {
      return text.split('\n').map(function (line) {
        var p = line.split('|');
        return { suburb: p[0], state: p[1], postcode: p[2], lower: p[0].toLowerCase(), label: p[0] + ' ' + p[1] + ' ' + p[2] };
      });
    }).catch(function (err) {
      suburbsPromise = null; // allow a retry on the next keystroke
      throw err;
    });
    return suburbsPromise;
  }
  function localSuggest(rows, qRaw, limit) {
    var q = String(qRaw || '').trim().toLowerCase();
    if (q.length < 2) return [];
    var isDigits = /^\d+$/.test(q);
    var out = [];
    for (var i = 0; i < rows.length && out.length < (limit || 8); i++) {
      var row = rows[i];
      if (isDigits ? row.postcode.indexOf(q) === 0 : row.lower.indexOf(q) === 0) out.push(row);
    }
    return out;
  }

  // The first quote otherwise pays the TCP+TLS handshake to Cloud Run
  // (~200-400ms) — pay it while the customer is still typing instead.
  var connectorWarmed = false;
  function warmConnector() {
    if (connectorWarmed) return;
    connectorWarmed = true;
    var w = anyWidget();
    if (!w || !w.dataset.endpoint) return;
    fetch(w.dataset.endpoint.replace(/\/quote$/, '/suggest') + '?q=warm', { mode: 'cors' }).catch(function () {});
  }

  // ---- 2b. quote context that SURVIVES drawer rebuilds ----
  // Pickup switches must write to the cart, and cart writes morph the drawer,
  // rebuilding the widget and wiping its closures. The customer's suburb,
  // email and quote mode live here so the fresh widget picks up where the
  // old one stopped (and re-quotes on its own).
  var CTX_KEY = 'hfQuoteCtx';
  function loadCtx() {
    try { return JSON.parse(sessionStorage.getItem(CTX_KEY) || '{}'); } catch (e) { return {}; }
  }
  function saveCtx(patch) {
    try {
      var ctx = loadCtx();
      for (var k in patch) ctx[k] = patch[k];
      sessionStorage.setItem(CTX_KEY, JSON.stringify(ctx));
    } catch (e) {}
  }

  // ---- 2c. quote cache ----
  // A destination+mode+goods combo that was quoted this session paints again
  // with ZERO network wait (Delivery↔Depot toggles, business flips, rebuild
  // re-quotes). 15-minute TTL keeps "live rate" honest; only ok quotes cache.
  var QCACHE_KEY = 'hfQuoteCache1';
  var QCACHE_TTL = 15 * 60 * 1000;
  function qcacheLoad() {
    try { return JSON.parse(sessionStorage.getItem(QCACHE_KEY) || '{}'); } catch (e) { return {}; }
  }
  function qcacheGet(key) {
    var hit = qcacheLoad()[key];
    return hit && Date.now() - hit.t < QCACHE_TTL ? hit.d : null;
  }
  function qcachePut(key, data) {
    try {
      var all = qcacheLoad();
      all[key] = { t: Date.now(), d: data };
      var keys = Object.keys(all);
      if (keys.length > 24) {
        keys.sort(function (a, b) { return all[a].t - all[b].t; });
        keys.slice(0, keys.length - 24).forEach(function (k) { delete all[k]; });
      }
      sessionStorage.setItem(QCACHE_KEY, JSON.stringify(all));
    } catch (e) {}
  }
  function quoteCacheKey(dest, residential, depot, fp) {
    return [dest.suburb, dest.state, dest.postcode, residential ? 'r' : 'b', depot ? 'd' : '-', fp].join('~');
  }
  // One quote pipe for the widget and the reconciler: cache first, network on
  // a miss, successful quotes cached for next time.
  function fetchQuote(endpoint, destination, items, cacheKey) {
    var hit = qcacheGet(cacheKey);
    if (hit) return Promise.resolve(hit);
    return postJSON(endpoint, { destination: destination, items: items }).then(function (d) {
      if (d && d.ok) qcachePut(cacheKey, d);
      return d;
    });
  }

  // ---- 3. reload with the drawer surviving (FALLBACK path) ----
  // refreshCart is the normal repair surface now; a reload only happens when
  // there is no cart-items-component to morph. A reload closes the slide-out
  // cart, so any reload we cause while it's open reopens it on the far side.
  var REOPEN_KEY = 'hfReopenDrawer';
  function reloadWithDrawer() {
    try {
      var dlg = document.querySelector('[data-cowork-cart-drawer] dialog');
      if (dlg && dlg.open) sessionStorage.setItem(REOPEN_KEY, '1');
    } catch (e) {}
    window.location.reload();
  }
  (function reopenDrawerIfAsked() {
    var want = null;
    try {
      want = sessionStorage.getItem(REOPEN_KEY);
      if (want) sessionStorage.removeItem(REOPEN_KEY);
    } catch (e) {}
    if (!want) return;
    var tries = 0;
    (function attempt() {
      var d = document.querySelector('[data-cowork-cart-drawer] cart-drawer-component');
      if (d && typeof d.open === 'function') { d.open(); return; }
      if (++tries < 20) setTimeout(attempt, 150);
    })();
  })();

  // ---- 4. freight follows the cart ----
  // The freight line is derived state: its quantity is the quoted dollars for
  // the exact goods it was quoted against. Whenever the goods drift (items
  // removed, quantities changed, cart emptied) this reconciler repairs it:
  // re-quote against the stored destination when possible, remove otherwise.
  var enforceBusy = false;
  var lastEnforce = 0;
  function enforceFreight(force) {
    var w = anyWidget();
    if (!w || enforceBusy) return;
    // Body-wide DOM churn calls this constantly — background passes are
    // rate-limited; real cart events pass force and run immediately.
    if (!force && Date.now() - lastEnforce < 2500) return;
    lastEnforce = Date.now();
    enforceBusy = true;
    var VARIANT = Number(w.dataset.variant);
    var done = function () { enforceBusy = false; };

    getCart(force === true).then(function (cart) {
      var freight = freightLinesOf(cart, VARIANT);
      var real = realItemsOf(cart, VARIANT);
      var attrs = cart.attributes || {};
      var fp = fingerprintOf(cart, VARIANT);

      if (!real.length) {
        // Cart emptied of goods: nothing to freight, nothing to quote.
        if (freight.length || attrs['Freight destination'] || attrs._freight_pickup) {
          return removeLines(freight)
            .then(function () { return setAttrs({ 'Freight destination': '', _freight_pickup: '', _freight_price: '', _freight_fingerprint: '', _freight_note: '' }); })
            .then(function () { refreshCart(cart); });
        }
        return;
      }
      if (attrs._freight_pickup) {
        // Pickup carts never carry a freight line.
        if (freight.length) return removeLines(freight).then(function () { refreshCart(cart); });
        return;
      }
      if (!freight.length) return;
      if (freight.length === 1 && attrs._freight_fingerprint === fp && Number(attrs._freight_price) === freight[0].quantity) return;

      // Goods changed under an added freight line. Reprice silently against
      // the destination the customer already chose; fall back to removing
      // freight with a "please recalculate" note for the widget to show.
      var m = String(attrs['Freight destination'] || '').match(/^(.+) ([A-Z]{2,3}) (\d{4}) \((home delivery|business address)\)$/);
      function removeAndAsk() {
        return removeLines(freight)
          .then(function () { return setAttrs({ 'Freight destination': '', _freight_price: '', _freight_fingerprint: '', _freight_note: 'requote' }); })
          .then(function () { refreshCart(cart); });
      }
      if (!m) return removeAndAsk();
      var dest = { suburb: m[1], state: m[2], postcode: m[3], residential: m[4] === 'home delivery' };
      return fetchQuote(
        w.dataset.endpoint,
        dest,
        quoteItemsOf(cart, VARIANT),
        quoteCacheKey(dest, dest.residential, false, fp)
      ).then(function (d) {
        if (!d.ok) return removeAndAsk();
        var priceChanged = freight[0].quantity !== d.price;
        var step = priceChanged
          ? postJSON('/cart/change.js', { id: freight[0].key, quantity: d.price })
          : Promise.resolve();
        return step
          .then(function () { return removeLines(freight.slice(1)); })
          .then(function () { return setAttrs({ _freight_price: String(d.price), _freight_fingerprint: fp }); })
          .then(function () {
            if (priceChanged || freight.length > 1) refreshCart(cart);
            else syncWidgets(); // silent fix: repaint so no copy shows stale state
          });
      }).catch(removeAndAsk);
    }).then(done, done);
  }

  // ---- 5. document-level singletons ----
  // Checkout gate: flooring carts may not reach checkout until freight is in
  // the cart OR pickup is chosen — the nudge repeats on EVERY click (Tynan's
  // call: flat rates under-collect, so no bypass). Two carts pass untouched:
  // freighted/pickup carts, and samples/accessories-only carts (no pallet
  // chip = nothing quotable; they ship on the flat sample rate).
  var checkoutBypass = false;
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.cart__checkout-button');
    if (!btn || checkoutBypass) return;
    var container = btn.closest('.cart-drawer__summary, .cart__summary-inner') || document;
    var w = container.querySelector('.hf-freight');
    if (!w || w.hidden) return;
    if (!w.querySelector('.hf-freight__chip')) return; // no flooring in cart
    e.preventDefault();
    e.stopPropagation();
    var pass = function () {
      checkoutBypass = true;
      btn.click();
      checkoutBypass = false;
    };
    getCart(true).then(function (c) {
      var hasFreight = c.items.some(function (i) { return String(i.variant_id) === w.dataset.variant; });
      if (hasFreight || (c.attributes && c.attributes._freight_pickup)) return pass();
      w.scrollIntoView({ block: 'center', behavior: 'smooth' });
      var quoted = !w.querySelector('.hf-freight__result').hidden;
      var search = w.querySelector('.hf-freight__search');
      if (search && !quoted && !w.querySelector('.hf-freight__form').hidden) search.focus();
      var err = w.querySelector('.hf-freight__error');
      err.textContent = quoted
        ? 'One tap to go — hit "Secure price & checkout" above to lock your freight in.'
        : 'Almost there — get your freight price first (type your suburb above), or switch to free Pickup.';
      err.hidden = false;
      err.classList.remove('hf-shake');
      void err.offsetWidth;
      err.classList.add('hf-shake');
    }).catch(pass);
  }, true);

  // ONE click-away closer for every widget copy (per-widget document
  // listeners used to accumulate across drawer morphs).
  document.addEventListener('click', function (e) {
    document.querySelectorAll('.hf-freight__suggests').forEach(function (list) {
      if (list.hidden) return;
      var root = list.closest('.hf-freight');
      if (root && !root.contains(e.target)) {
        list.hidden = true;
        list.innerHTML = '';
      }
    });
  });

  // bfcache restore: reconcile and repaint (single listener, not per-widget).
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    bustCart();
    enforceFreight(true);
    syncWidgets();
  });

  // ---- 6. recovery nets ----
  function initAll(forceEnforce) {
    var ctx = loadCtx();
    document.querySelectorAll('.hf-freight').forEach(function (r) {
      var s = r.querySelector('.hf-freight__search');
      if (!s) return;
      // One widget copy hitting an unexpected state must never take down the
      // whole script — and the nets below are armed before the first pass.
      if (!s._hfWired) {
        try { initFreightWidget(r); } catch (err) {}
        return; // fresh wiring restores its own context
      }
      // An in-place theme morph can wipe input values (server HTML renders
      // them empty) while the wired listeners survive. Restore the committed
      // pick/email — never while the customer is in the field, and never a
      // value they deliberately cleared (clearing also clears the context).
      if (!s.value && ctx.chosen && ctx.chosen.label && document.activeElement !== s) s.value = ctx.chosen.label;
      var em = r.querySelector('.hf-freight__email');
      if (em && !em.value && ctx.email && document.activeElement !== em) em.value = ctx.email;
    });
    enforceFreight(forceEnforce === true);
  }
  ['cart:update', 'cart:updated', 'cart:change'].forEach(function (ev) {
    document.addEventListener(ev, function () {
      bustCart();
      setTimeout(function () { initAll(true); }, 80);
    });
  });
  if ('MutationObserver' in window) {
    // Debounce with a MAX-WAIT: add-to-cart animations mutate the DOM for a
    // second straight, and a plain debounce would starve until they finish —
    // leaving the freshly morphed widget unwired under the customer's typing.
    var moTimer = null;
    var moLast = 0;
    var mo = new MutationObserver(function () {
      var now = Date.now();
      if (now - moLast > 400) { moLast = now; initAll(); return; }
      clearTimeout(moTimer);
      moTimer = setTimeout(function () { moLast = Date.now(); initAll(); }, 120);
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }
  setInterval(function () {
    if (document.hidden) return; // background tabs get rewired on return
    var unwired = false;
    document.querySelectorAll('.hf-freight').forEach(function (r) {
      var s = r.querySelector('.hf-freight__search');
      if (s && !s._hfWired) unwired = true;
    });
    if (unwired) initAll();
  }, 1500);
  initAll();

  // ---- 7. per-widget wiring ----
  // Klaviyo capture dedupe is MODULE-level: widget instances die on every
  // morph, and a per-instance key re-sent the same quote after each rebuild.
  var lastCaptureKey = '';
  function initFreightWidget(root) {
    var ENDPOINT = root.dataset.endpoint;
    var FREIGHT_VARIANT = Number(root.dataset.variant);

    var form = root.querySelector('.hf-freight__form');
    var result = root.querySelector('.hf-freight__result');
    var added = root.querySelector('.hf-freight__added');
    var pickupEl = root.querySelector('.hf-freight__pickup');
    var modesEl = root.querySelector('.hf-freight__modes');
    var amountEl = root.querySelector('.hf-freight__amount');
    var metaEl = result.querySelector('.hf-freight__meta');
    var addedText = root.querySelector('.hf-freight__added-text');
    var errorEl = root.querySelector('.hf-freight__error');
    var addBtn = root.querySelector('.hf-freight__add-btn');
    var searchEl = form.querySelector('.hf-freight__search');
    var listEl = form.querySelector('.hf-freight__suggests');
    var emailEl = form.querySelector('.hf-freight__email');
    var businessToggle = root.querySelector('input[name="hf_business"]');
    var fulfilBtns = root.querySelectorAll('.hf-freight__fulfil-btn');

    searchEl._hfWired = true;

    // Postcode bands where depot collection is pointless (they're near the
    // FREE warehouse pickup) — mirrors the server's authoritative check so
    // the Depot tab can refuse up front instead of dead-ending after a quote.
    var DEPOT_EXCLUDE = (root.dataset.depotExclude || '').split(',').map(function (band) {
      var p = band.split('-');
      return [Number(p[0]), Number(p[1] || p[0])];
    }).filter(function (b) { return b[0] > 0; });
    function depotExcluded(pc) {
      return DEPOT_EXCLUDE.some(function (b) { return +pc >= b[0] && +pc <= b[1]; });
    }
    var DEPOT_NEAR_MSG = 'Good news — you’re near our warehouse, so collection is FREE. Use the Pickup tab instead.';

    var lastQuote = null;       // { price, dest, mode, fingerprint, pallets, totalKg, depot? }
    var fulfilMode = 'delivery'; // 'delivery' | 'depot' | 'pickup' (client view state)
    var chosen = null;          // { suburb, state, postcode }
    var autoQuoted = false;     // restore-context auto-quote fires once per instance
    var quoteSeq = 0;           // stale-response guard: only the LATEST quote paints

    // Pick up where a rebuilt predecessor left off.
    (function restoreCtx() {
      var ctx = loadCtx();
      if (ctx.mode === 'depot') fulfilMode = 'depot';
      if (ctx.chosen && ctx.chosen.label) {
        chosen = ctx.chosen;
        searchEl.value = ctx.chosen.label;
      }
      if (ctx.email && emailEl && !emailEl.value) emailEl.value = ctx.email;
    })();
    var activeIdx = -1;
    var debounceTimer = null;
    var emailDebounce = null;

    function freightLines(cart) { return freightLinesOf(cart, FREIGHT_VARIANT); }
    function realItems(cart) { return realItemsOf(cart, FREIGHT_VARIANT); }
    function fingerprint(cart) { return fingerprintOf(cart, FREIGHT_VARIANT); }
    function removeFreight(cart) {
      return removeLines(freightLines(cart)).then(function () { return cart; });
    }

    // The swap area's height is MEASURED, not hardcoded: pickup/depot panels
    // min-height to the live delivery stack (form + gap + business toggle),
    // so the box never jumps between modes at any viewport or font size.
    function captureSwapHeight() {
      if (form.hidden || modesEl.hidden) return; // only measurable in delivery form state
      var fh = form.offsetHeight;
      var mh = modesEl.offsetHeight;
      if (!fh || !mh) return;
      var gap = parseFloat(getComputedStyle(root).gap) || 10;
      root.style.setProperty('--hf-swap-h', Math.round(fh + gap + mh) + 'px');
    }

    var fulfilPill = root.querySelector('.hf-freight__fulfil-pill');
    var pillPlaced = false;
    // Slide the white pill under the active tab. First placement is instant
    // (no fly-in from the left edge); every later change glides.
    function positionFulfilPill() {
      if (!fulfilPill) return;
      var active = root.querySelector('.hf-freight__fulfil-btn.is-active');
      if (!active || !active.offsetWidth) return; // hidden drawer — placed when first seen
      if (!pillPlaced) {
        fulfilPill.style.transition = 'none';
        fulfilPill.style.width = active.offsetWidth + 'px';
        fulfilPill.style.transform = 'translateX(' + active.offsetLeft + 'px)';
        pillPlaced = true;
        fulfilPill.parentElement.classList.add('is-placed');
        setTimeout(function () { fulfilPill.style.transition = ''; }, 50);
        return;
      }
      fulfilPill.style.width = active.offsetWidth + 'px';
      fulfilPill.style.transform = 'translateX(' + active.offsetLeft + 'px)';
    }
    function setFulfilTabs(mode) {
      fulfilBtns.forEach(function (b) { b.classList.toggle('is-active', b.dataset.mode === mode); });
      positionFulfilPill();
    }
    // Pickup point depends on WHAT'S IN THE CART (Tynan 28 Aug): hybrid and
    // timber only ever collect from Yatala; Artifloor vinyl collects from
    // Mansfield. cart.js has no tags, so vinyl is spotted by title/sku.
    function pickupDetails(cart) {
      var vinyl = false;
      var other = false;
      realItems(cart).forEach(function (i) {
        if (/luxury vinyl plank|\bAT\d{3,4}\b/i.test((i.product_title || '') + ' ' + (i.sku || ''))) vinyl = true;
        else other = true;
      });
      if (vinyl && other) return { loc: 'Yatala + Mansfield', meta: 'Hybrid and timber collects from our Yatala warehouse; vinyl collects from Mansfield (Brisbane). Choose "Pickup" at checkout.' };
      if (vinyl) return { loc: 'Mansfield', meta: 'Collect free from our vinyl warehouse in Mansfield (Brisbane), usually ready in 2-4 days. Choose "Pickup" at checkout.' };
      return { loc: 'Yatala', meta: 'Collect free from our Yatala warehouse, usually ready in 2-4 days. Choose "Pickup" at checkout.' };
    }
    function setPickupMeta(cart) {
      var m = pickupEl.querySelector('.hf-freight__meta');
      if (m) m.textContent = pickupDetails(cart).meta;
    }
    function show(state) {
      form.hidden = state !== 'form';
      result.hidden = state !== 'result';
      added.hidden = state !== 'added';
      pickupEl.hidden = state !== 'pickup';
      // Depot drops are always business deliveries — the toggle is moot there.
      modesEl.hidden = state === 'added' || state === 'pickup' || fulfilMode === 'depot';
      root.classList.toggle('hf-mode-depot', fulfilMode === 'depot');
    }
    function fail(msg) {
      errorEl.textContent = msg;
      errorEl.hidden = false;
      errorEl.classList.remove('hf-shake');
      void errorEl.offsetWidth; // restart the animation on repeat nudges
      errorEl.classList.add('hf-shake');
    }
    function clearError() { errorEl.hidden = true; }
    function setQuoted(on) {
      var container = root.closest('.cart-drawer__summary, .cart__summary-inner') || root.parentElement;
      if (container) container.classList.toggle('hf-quoted', on);
    }
    // Keep the totals honest: once a live quote is on screen, the Delivery row
    // shows it instead of "Calculated at checkout".
    function setDeliveryRow(text) {
      var container = root.closest('.cart-drawer__summary, .cart__summary-inner') || document;
      container.querySelectorAll('.cart__summary-row').forEach(function (row) {
        var spans = row.querySelectorAll('span');
        if (spans.length > 1 && /^Delivery/.test(spans[0].textContent.trim())) {
          spans[1].textContent = text;
        }
      });
    }

    // --- state paint (initial, bfcache, and hf-sync repaint requests) ---
    function syncState(fromSync) {
      if (modeSwitchBusy) return;
      // A repaint request must never steal the widget from an engaged
      // customer (typing, suggestion list open) — their view is still valid.
      if (fromSync === true && ((root.contains(document.activeElement) && !form.hidden) || !listEl.hidden)) return;
      addBtn.disabled = false;
      addBtn.textContent = 'Secure price & checkout';
      getCart().then(function (cart) {
        if (!realItems(cart).length) return; // emptied cart — enforceFreight cleans up
        root.hidden = false;
        var attrs = cart.attributes || {};
        if (attrs._freight_pickup) {
          fulfilMode = 'pickup'; // so leaving pickup later knows to clear the flag
          setFulfilTabs('pickup');
          setPickupMeta(cart);
          show('pickup');
          setQuoted(false);
          setDeliveryRow('Free pickup');
          return;
        }
        // Respect an in-progress depot choice (client-side state) on repaints.
        if (fulfilMode === 'pickup') fulfilMode = 'delivery';
        setFulfilTabs(fulfilMode);
        var lines = freightLines(cart);
        if (!lines.length) {
          show('form');
          captureSwapHeight();
          if (attrs._freight_note === 'requote') {
            setAttrs({ _freight_note: '' });
            fail('Your order changed — please recalculate freight.');
          }
          // Restored context re-quotes by itself: coming back from Pickup (or
          // any rebuild) should never mean retyping the suburb.
          if (!autoQuoted && chosen && emailEl && validEmail((emailEl.value || '').trim())) {
            autoQuoted = true;
            doQuote();
          }
          return;
        }
        if (attrs._freight_fingerprint === fingerprint(cart) && Number(attrs._freight_price) === lines[0].quantity) {
          var destAttr = attrs['Freight destination'] || 'your address';
          fulfilMode = destAttr.indexOf('(depot collection') > -1 ? 'depot' : 'delivery';
          setFulfilTabs(fulfilMode);
          addedText.textContent = 'Freight added: $' + lines[0].quantity + ' to ' + destAttr;
          show('added');
        }
        // else: goods changed under the freight line — enforceFreight
        // reprices (or removes) it and repaints, so leave the view alone.
      });
    }
    syncState();
    root.addEventListener('hf-sync', function () { syncState(true); });

    // Pulse the search field once, the first time this widget is actually
    // seen (page load for the cart page, drawer-open for the drawer copy).
    if ('IntersectionObserver' in window) {
      var attnObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !root.hidden) {
            root.classList.add('hf-freight--attn');
            positionFulfilPill(); // widget just became measurable (drawer opened)
            captureSwapHeight();
            attnObserver.disconnect();
          }
        });
      }, { threshold: 0.5 });
      attnObserver.observe(root);
    }

    // --- predictive suburb/postcode search (local index, instant) ---
    loadSuburbs().catch(function () {}); // warm the index before first keystroke
    function closeList() {
      listEl.hidden = true;
      listEl.innerHTML = '';
      activeIdx = -1;
      searchEl.setAttribute('aria-expanded', 'false');
    }
    function pick(sug) {
      chosen = sug;
      saveCtx({ chosen: sug });
      searchEl.value = sug.label;
      closeList();
      // Picked a near-warehouse suburb while on the Depot tab: bounce back to
      // Delivery with the why, instead of dead-ending on a server refusal.
      if (fulfilMode === 'depot' && depotExcluded(sug.postcode)) {
        fulfilMode = 'delivery';
        saveCtx({ mode: 'delivery' });
        setFulfilTabs('delivery');
        show('form');
        fail(DEPOT_NEAR_MSG);
        return;
      }
      maybeQuote();
    }
    function renderList(items) {
      if (!items.length) { closeList(); return; }
      listEl.innerHTML = '';
      items.forEach(function (sug) {
        var li = document.createElement('li');
        li.textContent = sug.label;
        li.setAttribute('role', 'option');
        li.addEventListener('mousedown', function (e) { e.preventDefault(); pick(sug); });
        listEl.appendChild(li);
      });
      listEl.hidden = false;
      activeIdx = -1;
      searchEl.setAttribute('aria-expanded', 'true');
    }
    function runSuggest() {
      var q = searchEl.value.trim();
      if (q.length < 2) { closeList(); return; }
      loadSuburbs().then(function (rows) {
        if (searchEl.value.trim() === q) renderList(localSuggest(rows, q, 8));
      }).catch(function () {
        // Index unavailable (blocked CDN?) — fall back to the live endpoint.
        fetch(ENDPOINT.replace(/\/quote$/, '/suggest') + '?q=' + encodeURIComponent(q))
          .then(function (r) { return r.json(); })
          .then(function (d) { if (searchEl.value.trim() === q) renderList(d.suggestions || []); })
          .catch(closeList);
      });
    }
    searchEl.addEventListener('input', function () {
      chosen = null;
      saveCtx({ chosen: null });
      warmConnector();
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(runSuggest, 60);
    });
    // Recovery: if a rebuild orphaned typed text (value survives, closure
    // state doesn't), focusing the field brings the suggestions back.
    searchEl.addEventListener('focus', function () {
      warmConnector();
      if (!chosen && searchEl.value.trim().length >= 2) runSuggest();
    });
    searchEl.addEventListener('keydown', function (e) {
      var items = listEl.querySelectorAll('li');
      if (listEl.hidden || !items.length) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        activeIdx = e.key === 'ArrowDown' ? Math.min(activeIdx + 1, items.length - 1) : Math.max(activeIdx - 1, 0);
        items.forEach(function (li, i) { li.setAttribute('aria-selected', i === activeIdx ? 'true' : 'false'); });
      } else if (e.key === 'Enter' && activeIdx >= 0) {
        e.preventDefault();
        items[activeIdx].dispatchEvent(new MouseEvent('mousedown'));
      } else if (e.key === 'Escape') {
        closeList();
      }
    });

    // --- quoting (runs automatically once suburb picked + email present) ---
    function validEmail(v) { return /.+@.+\..+/.test(v); }
    function deliveryMode() {
      return businessToggle && businessToggle.checked ? 'business' : 'home';
    }
    function maybeQuote() {
      if (!chosen) return;
      var email = emailEl ? (emailEl.value || '').trim() : '';
      if (!validEmail(email)) {
        show('form');
        emailEl.focus();
        fail('Pop your email in to see your live price.');
        return;
      }
      clearError();
      doQuote();
    }
    // Success paint shared by the network and cache paths.
    function paintQuote(data, dest, residential, depotMode, fp) {
      result.classList.remove('hf-loading');
      lastQuote = {
        price: data.price,
        dest: dest.suburb + ' ' + dest.state + ' ' + dest.postcode,
        mode: depotMode ? 'depot collection' : (residential ? 'home delivery' : 'business address'),
        fingerprint: fp,
        pallets: data.pallets,
        totalKg: data.totalKg,
        depot: data.depot || null,
      };
      amountEl.textContent = '$' + data.price.toFixed(0) + '.00';
      // Customer-facing strings never name the carrier (house rule) —
      // the depot line shows locality only; the street rides on the order.
      var depotLocality = data.depot ? data.depot.address.split(', ').slice(1).join(', ') : '';
      metaEl.textContent = depotMode && data.depot
        ? 'Collect from your nearest depot · ' + depotLocality + ' · inc GST'
        : (residential ? 'Home delivery' : 'Business address') + ' to ' + lastQuote.dest + ' · inc GST';
      setDeliveryRow('$' + data.price.toFixed(0) + '.00');
      var capEmail = emailEl ? (emailEl.value || '').trim() : '';
      var capKey = capEmail + '|' + lastQuote.dest + '|' + lastQuote.mode + '|' + data.price;
      if (validEmail(capEmail) && capKey !== lastCaptureKey) {
        lastCaptureKey = capKey;
        try { sendQuoteToKlaviyo(capEmail); } catch (e) {}
      }
      show('result');
    }
    function showQuoteLoading() {
      amountEl.innerHTML = '<span class="hf-dots" aria-label="Getting your price"><span></span><span></span><span></span></span>';
      metaEl.textContent = 'Getting your live freight price…';
      result.classList.add('hf-loading'); // live-rate line waits for the price
    }
    function doQuote() {
      clearError();
      var residential = deliveryMode() === 'home';
      if (!chosen) {
        fail('Start typing your suburb or postcode, then pick it from the list.');
        return;
      }
      var dest = chosen;
      var depotMode = fulfilMode === 'depot';
      var seq = ++quoteSeq; // later quotes invalidate this one's paint
      addBtn.disabled = true;
      show('result');
      setQuoted(true);
      // Optimistic instant paint: this destination+mode+goods combo was
      // quoted before (session cache) → show the price with ZERO wait. The
      // cart fetch below re-checks the goods fingerprint; on drift the real
      // quote runs and paints over.
      var painted = false;
      if (lastCartFp !== null) {
        var pre = qcacheGet(quoteCacheKey(dest, residential, depotMode, lastCartFp));
        if (pre) {
          painted = true;
          paintQuote(pre, dest, residential, depotMode, lastCartFp);
        }
      }
      if (!painted) showQuoteLoading();
      getCart().then(function (cart) {
        if (seq !== quoteSeq) return; // superseded by a newer quote
        var fp = fingerprint(cart);
        if (painted && lastQuote && fp === lastQuote.fingerprint) return; // cache confirmed
        if (painted) showQuoteLoading(); // goods drifted — honest re-quote
        return fetchQuote(
          ENDPOINT,
          { suburb: dest.suburb, state: dest.state, postcode: dest.postcode, residential: residential, depot: depotMode },
          quoteItemsOf(cart, FREIGHT_VARIANT),
          quoteCacheKey(dest, residential, depotMode, fp)
        ).then(function (data) {
          if (seq !== quoteSeq) return;
          result.classList.remove('hf-loading');
          if (!data.ok) {
            fail(data.error || 'Could not get a quote.');
            show('form');
            setQuoted(false);
            setDeliveryRow('Calculated at checkout');
            return;
          }
          paintQuote(data, dest, residential, depotMode, fp);
        });
      }).catch(function () {
        if (seq !== quoteSeq) return;
        result.classList.remove('hf-loading');
        fail('Could not reach the freight service — please try again in a moment.');
        show('form');
        setQuoted(false);
      }).then(function () { addBtn.disabled = false; }, function () { addBtn.disabled = false; });
    }
    form.addEventListener('submit', function (e) { e.preventDefault(); maybeQuote(); });
    if (businessToggle) {
      businessToggle.addEventListener('change', function () { if (chosen) doQuote(); });
    }
    if (emailEl) {
      emailEl.addEventListener('input', function () {
        clearTimeout(emailDebounce);
        emailDebounce = setTimeout(function () {
          saveCtx({ email: (emailEl.value || '').trim() });
          if (chosen && validEmail((emailEl.value || '').trim()) && result.hidden) maybeQuote();
        }, 500);
      });
      emailEl.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); maybeQuote(); }
      });
    }

    // --- add freight & checkout ---
    addBtn.addEventListener('click', function () {
      if (!lastQuote) return;
      addBtn.disabled = true;
      addBtn.textContent = 'Adding freight…';
      getCart(true)
        .then(function (cart) {
          if (fingerprint(cart) !== lastQuote.fingerprint) {
            fail('Your order changed — please recalculate freight.');
            show('form');
            throw new Error('stale');
          }
          return removeFreight(cart);
        })
        .then(function () {
          // No carrier name in the attribute either — it shows on the order
          // status page. Locality + street is everything ops needs to book.
          var destAttr = lastQuote.depot
            ? lastQuote.dest + ' (depot collection: ' + lastQuote.depot.address.split(', ').slice(1).join(', ') + ', ' + lastQuote.depot.address.split(', ')[0] + ')'
            : lastQuote.dest + ' (' + lastQuote.mode + ')';
          return setAttrs({
            'Freight destination': destAttr,
            _freight_pickup: '',
            _freight_price: String(lastQuote.price),
            _freight_fingerprint: lastQuote.fingerprint,
          });
        })
        .then(function () {
          return postJSON('/cart/add.js', { id: FREIGHT_VARIANT, quantity: lastQuote.price });
        })
        .then(function () {
          var STATES = { QLD: 'Queensland', NSW: 'New South Wales', VIC: 'Victoria', SA: 'South Australia', WA: 'Western Australia', TAS: 'Tasmania', NT: 'Northern Territory', ACT: 'Australian Capital Territory' };
          var parts = lastQuote.dest.match(/^(.*) ([A-Z]{2,3}) (\d{4})$/);
          var qs = '';
          if (parts) {
            qs = '?checkout[shipping_address][city]=' + encodeURIComponent(parts[1]) +
                 '&checkout[shipping_address][province]=' + encodeURIComponent(STATES[parts[2]] || parts[2]) +
                 '&checkout[shipping_address][zip]=' + encodeURIComponent(parts[3]) +
                 '&checkout[shipping_address][country]=Australia' +
                 '&checkout[shipping_address][address1]=&checkout[shipping_address][address2]=';
          }
          // A typed quote-email rides along: prefills checkout's contact
          // field so it never has to be typed twice.
          var typedEmail = emailEl ? (emailEl.value || '').trim() : '';
          if (validEmail(typedEmail)) {
            qs += (qs ? '&' : '?') + 'checkout[email]=' + encodeURIComponent(typedEmail);
          }
          window.location.href = '/checkout' + qs;
        })
        .catch(function (err) {
          if (String(err && err.message) !== 'stale') fail('Could not add freight — please try again.');
          addBtn.disabled = false;
          addBtn.textContent = 'Secure price & checkout';
        });
    });

    // --- Delivery / Pickup segmented control ---
    // The pill swaps INSTANTLY on click (feedback first), then the cart
    // catches up; if a freight line had to come off, the drawer refreshes
    // in place (no reload — the theme morphs the fresh section in).
    fulfilBtns.forEach(function (b) {
      b.addEventListener('click', function () {
        if (b.classList.contains('is-active')) return;
        captureSwapHeight(); // measure the delivery stack while it's rendered
        var mode = b.dataset.mode;
        var prev = fulfilMode;
        // A customer near the free warehouse never gets Depot: refuse the
        // switch on the spot, explain why, and stay on the current tab.
        if (mode === 'depot' && chosen && depotExcluded(chosen.postcode)) {
          fail(DEPOT_NEAR_MSG);
          return;
        }
        setFulfilTabs(mode);
        clearError();
        if (mode === 'pickup') {
          fulfilMode = 'pickup';
          show('pickup');
          setQuoted(false);
          setDeliveryRow('Free pickup');
          modeSwitchBusy = true;
          pendingSwitches++;
          var settle = function () {
            if (--pendingSwitches > 0) return; // later clicks still writing
            modeSwitchBusy = false;
            syncWidgets();
          };
          var work = function () {
            return getCart(true).then(function (cart) {
              var hadFreight = freightLines(cart).length > 0;
              var pd = pickupDetails(cart);
              setPickupMeta(cart);
              return removeFreight(cart)
                .then(function () {
                  return setAttrs({ 'Freight destination': 'Local pickup (customer collects: ' + pd.loc + ')', _freight_pickup: 'yes', _freight_price: '', _freight_fingerprint: '' });
                })
                .then(function () { if (hadFreight) refreshCart(cart); });
            });
          };
          modeWriteChain = modeWriteChain.then(work, work).then(settle, settle);
          return;
        }
        // Delivery and depot are both quote modes. Toggling between them is a
        // PURE CLIENT switch — a picked suburb re-quotes in the new mode on
        // the spot. Any cart write here would trigger a drawer morph that
        // rebuilds the widget and wipes the customer's suburb mid-toggle, so
        // the pickup flag only gets cleared when actually LEAVING pickup.
        fulfilMode = mode;
        saveCtx({ mode: mode });
        show('form');
        setQuoted(false);
        setDeliveryRow('Calculated at checkout');
        if (prev === 'pickup') {
          modeSwitchBusy = true;
          pendingSwitches++;
          var settleQ = function () {
            if (--pendingSwitches > 0) return;
            modeSwitchBusy = false;
            syncWidgets();
          };
          var clearWork = function () {
            return setAttrs({ 'Freight destination': '', _freight_pickup: '' });
          };
          modeWriteChain = modeWriteChain.then(clearWork, clearWork).then(settleQ, settleQ);
        }
        if (chosen) setTimeout(maybeQuote, 0);
      });
    });

    // --- change / remove ---
    root.querySelector('.hf-freight__change').addEventListener('click', function () {
      clearError();
      show('form');
      setQuoted(false);
      setDeliveryRow('Calculated at checkout');
    });
    root.querySelector('.hf-freight__remove').addEventListener('click', function () {
      clearError();
      getCart(true)
        .then(function (cart) {
          return removeFreight(cart)
            .then(function () {
              return setAttrs({ 'Freight destination': '', _freight_price: '', _freight_fingerprint: '' });
            })
            .then(function () { refreshCart(cart); });
        });
    });

    // --- Klaviyo quote capture (identifies the profile + fires the
    //     "Freight Quote Emailed" metric; the Klaviyo flow sends the email) ---
    function sendQuoteToKlaviyo(email) {
      if (!lastQuote) return Promise.resolve();
      return fetch('https://a.klaviyo.com/client/events/?company_id=Wk5Aiv', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              properties: {
                price: lastQuote.price,
                destination: lastQuote.dest,
                mode: lastQuote.mode,
                pallets: lastQuote.pallets,
                totalKg: lastQuote.totalKg,
                url: location.href,
              },
              metric: { data: { type: 'metric', attributes: { name: 'Freight Quote Emailed' } } },
              profile: { data: { type: 'profile', attributes: { email: email } } },
            },
          },
        }),
      });
    }

    // Keystrokes can land BEFORE wiring (input values persist without
    // listeners) — surface whatever's already typed the moment we go live.
    if (searchEl.value.trim().length >= 2 && document.activeElement === searchEl) runSuggest();
  }
})();
